import axios from 'axios';

const DEFAULT_API_URL = 'http://localhost:8900/api/v1';

const normalizeApiUrl = (value?: string | null): string => {
  let normalized = value?.trim();

  if (!normalized) {
    return DEFAULT_API_URL;
  }

  normalized = normalized.replace(/\\/g, '/');

  if (!/^https?:/i.test(normalized)) {
    normalized = `http://${normalized}`;
  }

  normalized = normalized.replace(/^https?:\/*/i, (protocol) =>
    protocol.toLowerCase().startsWith('https') ? 'https://' : 'http://'
  );

  normalized = normalized.replace(/:(\d{2,5})([A-Za-z])/g, ':$1/$2');

  try {
    const url = new URL(normalized);
    url.protocol = url.protocol === 'https:' ? 'https:' : 'http:';
    url.pathname = url.pathname.replace(/\/{2,}/g, '/');
    if (url.pathname !== '/' && url.pathname.endsWith('/')) {
      url.pathname = url.pathname.slice(0, -1);
    }
    return url.toString();
  } catch {
    return DEFAULT_API_URL;
  }
};

const API_URL = normalizeApiUrl(import.meta.env.VITE_API_URL);

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const BACKEND_UNAVAILABLE_EVENT = 'backend-unavailable';
const BACKEND_UNAVAILABLE_EVENT_COOLDOWN_MS = 15000;
let lastBackendUnavailableEventAt = 0;

const notifyBackendUnavailable = (status?: number) => {
  if (typeof window === 'undefined') return;

  const now = Date.now();
  if (now - lastBackendUnavailableEventAt < BACKEND_UNAVAILABLE_EVENT_COOLDOWN_MS) {
    return;
  }

  lastBackendUnavailableEventAt = now;
  window.dispatchEvent(
    new CustomEvent(BACKEND_UNAVAILABLE_EVENT, {
      detail: { status, timestamp: new Date().toISOString() },
    }),
  );
};

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const activeEmpresaId = sessionStorage.getItem('active_empresa_id');
  if (activeEmpresaId) {
    config.headers['X-Empresa-Id'] = activeEmpresaId;
  } else if (config.headers && 'X-Empresa-Id' in config.headers) {
    delete config.headers['X-Empresa-Id'];
  }
  return config;
});

// Controle de refresh concorrente: evita múltiplas chamadas simultâneas de /auth/refresh
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token as string);
    }
  });
  failedQueue = [];
};

const redirectToLogin = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
  window.dispatchEvent(new CustomEvent('session-expired'));
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status as number | undefined;
    const errorData = error.response?.data as { detail?: string; message?: string } | undefined;
    const hasBackendMessage = Boolean(errorData?.detail || errorData?.message);
    const isNetworkFailure =
      !error.response && (error.code === 'ERR_NETWORK' || /network error/i.test(error.message || ''));
    const isGatewayFailure = (status === 502 || status === 503 || status === 504) && !hasBackendMessage;

    if (isNetworkFailure || isGatewayFailure) {
      notifyBackendUnavailable(status);
    }

    // Log detalhado de erros 422 para facilitar diagnóstico
    if (status === 422) {
      const data = error.response?.data as { details?: unknown[]; message?: string } | undefined;
      console.warn(
        '[422 Validation Error]',
        error.config?.method?.toUpperCase(),
        error.config?.url,
        '\nDetails:', data?.details ?? data,
      );
    }

    const originalRequest = error.config;

    // Só tenta refresh em respostas 401 que ainda não foram retentadas
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      redirectToLogin();
      return Promise.reject(error);
    }

    // Se já está em processo de refresh, enfileira esta request
    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        })
        .catch((err) => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      // Usa axios diretamente para não passar pelo interceptor de request (evita loop)
      const response = await axios.post(
        `${API_URL}/auth/refresh`,
        {},
        { headers: { Authorization: `Bearer ${refreshToken}` } }
      );
      const { access_token, refresh_token: newRefreshToken } = response.data.data;

      localStorage.setItem('access_token', access_token);
      if (newRefreshToken) {
        localStorage.setItem('refresh_token', newRefreshToken);
      }

      // Atualiza o header padrão para requests futuras
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      processQueue(null, access_token);

      originalRequest.headers.Authorization = `Bearer ${access_token}`;
      return api(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      redirectToLogin();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);
